<template>
  <div class="admin-products-view">
    <div v-if="!hasSupportAccess" class="access-denied-card">
      <div class="access-denied-icon"><i class="fa-solid fa-lock"></i></div>
      <h3>Access restricted</h3>
      <p>Only super admin or support can view and manage seller listings.</p>
      <p class="access-denied-hint">Sign in with an admin account that has access to support tools.</p>
    </div>

    <div v-else>
    <!-- HEADER -->
    <div class="admin-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fa-solid fa-store"></i>
          Product Listings Management
        </h1>
        <p class="page-subtitle">Manage all seller product listings</p>
      </div>
      <div class="header-stats">
        <div class="stat-card">
          <i class="fa-solid fa-boxes"></i>
          <div>
            <span class="stat-number">{{ totalListings }}</span>
            <span class="stat-label">Total Listings</span>
          </div>
        </div>
        <div class="stat-card">
          <i class="fa-solid fa-check-circle"></i>
          <div>
            <span class="stat-number">{{ activeListings }}</span>
            <span class="stat-label">Active</span>
          </div>
        </div>
        <div class="stat-card">
          <i class="fa-solid fa-pause-circle"></i>
          <div>
            <span class="stat-number">{{ inactiveListings }}</span>
            <span class="stat-label">Inactive</span>
          </div>
        </div>
      </div>
    </div>

    <!-- CONTROLS & FILTERS -->
    <div class="controls-section">
      <div class="search-filter-group">
        <div class="search-box">
          <i class="fa-solid fa-magnifying-glass"></i>
          <input 
            type="text" 
            v-model="searchQuery"
            placeholder="Search listings by title, seller, or category..."
            @input="performSearch"
          />
          <button 
            class="clear-btn" 
            @click="clearSearch"
            v-if="searchQuery"
          >
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="filter-group">
          <div class="filter-select">
            <i class="fa-solid fa-filter"></i>
            <select v-model="statusFilter" @change="applyFilters">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="sold">Sold</option>
            </select>
          </div>

          <div class="filter-select">
            <i class="fa-solid fa-tag"></i>
            <select v-model="categoryFilter" @change="applyFilters">
              <option value="">All Categories</option>
              <option v-for="category in categories" :value="category" :key="category">
                {{ category }}
              </option>
            </select>
          </div>

          <div class="filter-select">
            <i class="fa-solid fa-sort"></i>
            <select v-model="sortBy" @change="applyFilters">
              <option value="recent">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="price-high">Price High</option>
              <option value="price-low">Price Low</option>
            </select>
          </div>
        </div>
      </div>

      <div class="action-buttons">
        <button class="btn-export" @click="exportData">
          <i class="fa-solid fa-file-export"></i>
          Export
        </button>
        <button class="btn-refresh" @click="refreshData">
          <i class="fa-solid fa-sync-alt"></i>
          Refresh
        </button>
      </div>
    </div>

    <!-- MAIN CONTENT -->
    <div class="content-wrapper">
      <!-- LOADING STATE -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner">
          <i class="fa-solid fa-spinner fa-spin"></i>
        </div>
        <p>Loading product listings...</p>
      </div>

      <!-- EMPTY STATE -->
      <div v-else-if="filteredListings.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="fa-solid fa-box-open"></i>
        </div>
        <h3>No Listings Found</h3>
        <p>Try adjusting your filters or search terms</p>
        <button class="btn-clear-filters" @click="clearAllFilters">
          <i class="fa-solid fa-undo"></i>
          Clear All Filters
        </button>
      </div>

      <!-- LISTINGS TABLE WITH HORIZONTAL SCROLL -->
      <div v-else class="listings-container">
        <div class="table-scroll-wrapper">
          <div class="table-view">
            <div class="table-header">
              <div class="table-row header-row">
                <div class="table-cell product-col" @click="sortByField('title')">
                  <span>Product</span>
                  <i class="fa-solid fa-sort"></i>
                </div>
                <div class="table-cell seller-col" @click="sortByField('seller.name')">
                  <span>Seller</span>
                  <i class="fa-solid fa-sort"></i>
                </div>
                <div class="table-cell category-col" @click="sortByField('category')">
                  <span>Category</span>
                  <i class="fa-solid fa-sort"></i>
                </div>
                <div class="table-cell price-col" @click="sortByField('price')">
                  <span>Price</span>
                  <i class="fa-solid fa-sort"></i>
                </div>
                <div class="table-cell condition-col" @click="sortByField('condition')">
                  <span>Condition</span>
                  <i class="fa-solid fa-sort"></i>
                </div>
                <div class="table-cell status-col">
                  <span>Status</span>
                </div>
                <div class="table-cell analytics-col">
                  <span>Analytics</span>
                </div>
                <div class="table-cell actions-col">
                  <span>Actions</span>
                </div>
              </div>
            </div>

            <div class="table-body">
              <div 
                v-for="listing in paginatedListings" 
                :key="listing.listing_id"
                class="table-row listing-row"
                :class="{ 'row-expanded': expandedRow === listing.listing_id }"
              >
                <!-- PRODUCT COLUMN -->
                <div class="table-cell product-col">
                  <div class="product-info-compact">
                    <div class="product-image-compact" @click="previewImages(listing)">
                      <img
                        :src="getCoverImage(listing)"
                        :alt="listing.title"
                        loading="lazy"
                        decoding="async"
                        @error="handleImageError"
                      />
                      <div v-if="listing.images && listing.images.length > 1" class="image-count-compact">
                        <i class="fa-solid fa-images"></i>
                        <span>{{ listing.images.length }}</span>
                      </div>
                    </div>
                    <div class="product-details-compact">
                      <h4 class="product-title-compact">{{ listing.title }}</h4>
                      <p class="product-brand-compact">
                        <i class="fa-solid fa-tag"></i>
                        {{ listing.brand }}
                      </p>
                      <div class="product-meta-compact">
                        <span class="meta-item-compact">
                          <i class="fa-regular fa-clock"></i>
                          {{ listing.age }}
                        </span>
                        <span class="meta-item-compact">
                          <i class="fa-solid fa-location-dot"></i>
                          {{ listing.location }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- SELLER COLUMN -->
                <div class="table-cell seller-col">
                  <div class="seller-info-compact">
                    <div class="seller-avatar-compact">
                      <img
                        :src="listing.seller.picture || getDefaultAvatar()"
                        :alt="listing.seller.name"
                        loading="lazy"
                        decoding="async"
                        @error="handleSellerImageError"
                      />
                      <div 
                        class="verified-badge-compact" 
                        v-if="listing.seller.verified"
                        :title="'Verified Seller'"
                      >
                        <i class="fa-solid fa-check-circle"></i>
                      </div>
                    </div>
                    <div class="seller-details-compact">
                      <h5 class="seller-name-compact">{{ listing.seller.name }}</h5>
                      <div class="seller-rating-compact">
                        <i class="fa-solid fa-star"></i>
                        <span>{{ listing.seller.rating }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- CATEGORY COLUMN -->
                <div class="table-cell category-col">
                  <span class="category-badge-compact">{{ listing.category }}</span>
                </div>

                <!-- PRICE COLUMN -->
                <div class="table-cell price-col">
                  <div class="price-amount-compact">KES {{ formatPrice(listing.price) }}</div>
                  <div v-if="listing.negotiable" class="negotiable-badge-compact">
                    <i class="fa-solid fa-handshake"></i>
                    Neg.
                  </div>
                </div>

                <!-- CONDITION COLUMN -->
                <div class="table-cell condition-col">
                  <span class="condition-badge-compact" :class="getConditionClass(listing.condition)">
                    {{ listing.condition }}
                  </span>
                </div>

                <!-- STATUS COLUMN -->
                <div class="table-cell status-col">
                  <span class="status-badge-compact" :class="getStatusClass(listing.status)">
                    <i :class="getStatusIcon(listing.status)"></i>
                    {{ getStatusText(listing.status) }}
                  </span>
                </div>

                <!-- ANALYTICS COLUMN (views + saves from API) -->
                <div class="table-cell analytics-col">
                  <div class="analytics-compact">
                    <div class="analytic-item-compact" title="Listing views">
                      <i class="fa-solid fa-eye"></i>
                      <span>{{ formatCount(listing.views) }}</span>
                    </div>
                    <div class="analytic-item-compact" title="Saves">
                      <i class="fa-solid fa-heart"></i>
                      <span>{{ formatCount(listing.save_count) }}</span>
                    </div>
                  </div>
                </div>

                <!-- ACTIONS COLUMN -->
                <div class="table-cell actions-col">
                  <div class="action-buttons-compact">
                    <button 
                      class="btn-action-compact view-btn" 
                      @click="toggleRowExpansion(listing.listing_id)"
                      :title="expandedRow === listing.listing_id ? 'Collapse' : 'View details'"
                    >
                      <i :class="expandedRow === listing.listing_id ? 'fa-solid fa-chevron-up' : 'fa-solid fa-eye'"></i>
                    </button>
                  </div>
                </div>

                <!-- EXPANDED ROW DETAILS -->
                <transition name="slide-down">
                  <div v-if="expandedRow === listing.listing_id" class="expanded-details-compact">
                    <div class="details-grid-compact">
                      <div class="detail-section-compact">
                        <h6><i class="fa-solid fa-align-left"></i> Description</h6>
                        <div class="description-content-compact">
                          <p><strong>Short:</strong> {{ listing.short_description || 'No description' }}</p>
                          <p><strong>Full:</strong> {{ listing.full_description || 'No detailed description' }}</p>
                        </div>
                      </div>
                      
                      <div class="detail-section-compact">
                        <h6><i class="fa-solid fa-image"></i> Images ({{ listing.images?.length || 0 }})</h6>
                        <div class="image-gallery-compact">
                          <div 
                            v-for="(image, index) in listing.images" 
                            :key="index"
                            class="gallery-thumbnail-compact"
                            :class="{ 'active': imagePreview?.listing_id === listing.listing_id && imagePreview?.index === index }"
                            @click="previewSpecificImage(listing, index)"
                          >
                            <img
                              :src="image"
                              :alt="`Image ${index + 1}`"
                              loading="lazy"
                              decoding="async"
                            />
                            <span class="thumbnail-number-compact">{{ index + 1 }}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div class="detail-section-compact">
                        <h6><i class="fa-solid fa-chart-line"></i> Analytics</h6>
                        <div class="analytics-grid-compact">
                          <div class="analytic-item-expanded">
                            <i class="fa-solid fa-eye"></i>
                            <div>
                              <span class="analytic-value-expanded">{{ formatCount(listing.views) }}</span>
                              <span class="analytic-label-expanded">Views</span>
                            </div>
                          </div>
                          <div class="analytic-item-expanded">
                            <i class="fa-solid fa-heart"></i>
                            <div>
                              <span class="analytic-value-expanded">{{ formatCount(listing.save_count) }}</span>
                              <span class="analytic-label-expanded">Saves</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div class="detail-section-compact">
                        <h6><i class="fa-solid fa-info-circle"></i> Listing Info</h6>
                        <div class="info-grid-compact">
                          <div class="info-item-compact">
                            <i class="fa-solid fa-hashtag"></i>
                            <span>ID:</span>
                            <span>{{ listing.listing_id }}</span>
                          </div>
                          <div class="info-item-compact">
                            <i class="fa-solid fa-calendar-plus"></i>
                            <span>Created:</span>
                            <span>{{ formatListingDate(listing.created_at || listing.listing_created_at) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div class="detail-actions-compact">
                      <button type="button" class="btn-secondary-compact" @click="toggleRowExpansion(listing.listing_id)">
                        <i class="fa-solid fa-times"></i>
                        Close
                      </button>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>

        <!-- PAGINATION -->
        <div class="pagination-compact" v-if="filteredListings.length > itemsPerPage">
          <div class="pagination-info">
            Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ filteredListings.length }}
          </div>
          <div class="pagination-controls">
            <button 
              class="pagination-btn-compact" 
              @click="prevPage"
              :disabled="currentPage === 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            
            <div class="page-numbers-compact">
              <button 
                v-for="page in visiblePages" 
                :key="page"
                class="page-number-compact"
                :class="{ active: page === currentPage }"
                @click="goToPage(page)"
              >
                {{ page }}
              </button>
            </div>
            
            <button 
              class="pagination-btn-compact" 
              @click="nextPage"
              :disabled="currentPage === totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- IMAGE PREVIEW MODAL -->
    <transition name="modal">
      <div v-if="imagePreview" class="image-preview-modal" @click.self="closeImagePreview">
        <div class="modal-content">
          <div class="modal-header">
            <h3>Image Preview</h3>
            <button class="modal-close" @click="closeImagePreview">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <div class="preview-image-container">
              <img
                :src="imagePreview.imageUrl"
                :alt="`Image ${imagePreview.index + 1}`"
                decoding="async"
              />
              <div class="image-nav">
                <button 
                  class="nav-btn prev-btn" 
                  @click="navigateImage(-1)"
                  :disabled="!hasPrevImage"
                >
                  <i class="fa-solid fa-chevron-left"></i>
                </button>
                <span class="image-counter">
                  {{ imagePreview.index + 1 }} / {{ imagePreview.total }}
                </span>
                <button 
                  class="nav-btn next-btn" 
                  @click="navigateImage(1)"
                  :disabled="!hasNextImage"
                >
                  <i class="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

        </div>
  </div>
</template>

<script>
import { useAdminAuthStore } from "@/authen/adminAuth";
import globalToastMixin from "@/mixins/globalToastMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: 'AdminProductListings',
  mixins: [globalToastMixin],
  
  data() {
    return {
      loading: true,
      listings: [],
      searchQuery: '',
      statusFilter: '',
      categoryFilter: '',
      sortBy: 'recent',
      sortDirection: 'desc',
      currentPage: 1,
      itemsPerPage: 10,
      expandedRow: null,
      imagePreview: null,
      categories: [
        'Football', 'Basketball', 'Gym & Fitness', 'Swimming',
        'Cycling', 'Outdoor & Hiking', 'Tennis', 'Running',
        'Yoga', 'Martial Arts', 'Winter Sports', 'Water Sports'
      ]
    };
  },

  computed: {
    hasSupportAccess() {
      const store = useAdminAuthStore();
      return store.canAccessSupportAdminTools;
    },
    filteredListings() {
      let filtered = this.listings;
      
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(listing => 
          listing.title.toLowerCase().includes(query) ||
          listing.seller.name.toLowerCase().includes(query) ||
          listing.category.toLowerCase().includes(query) ||
          listing.brand.toLowerCase().includes(query)
        );
      }
      
      if (this.statusFilter) {
        filtered = filtered.filter(listing => listing.status === this.statusFilter);
      }
      
      if (this.categoryFilter) {
        filtered = filtered.filter(listing => listing.category === this.categoryFilter);
      }
      
      filtered = this.sortListings(filtered);
      
      return filtered;
    },
    
    paginatedListings() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredListings.slice(start, end);
    },
    
    totalListings() {
      return this.listings.length;
    },
    
    activeListings() {
      return this.listings.filter(l => l.status === 'active').length;
    },

    inactiveListings() {
      return this.listings.filter(l => l.status === 'inactive').length;
    },
    
    totalPages() {
      return Math.ceil(this.filteredListings.length / this.itemsPerPage);
    },
    
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.filteredListings.length);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    },
    
    hasPrevImage() {
      return this.imagePreview && this.imagePreview.index > 0;
    },
    
    hasNextImage() {
      return this.imagePreview && this.imagePreview.index < this.imagePreview.total - 1;
    }
  },

  mounted() {
    const store = useAdminAuthStore();
    store.checkToken();
    if (this.hasSupportAccess) {
      this.fetchListings();
    } else {
      this.loading = false;
    }
  },

  methods: {
    getAdminHeaders(json = false) {
      const store = useAdminAuthStore();
      store.checkToken();
      const headers = { Accept: "application/json" };
      if (json) headers["Content-Type"] = "application/json";
      const token = store.accessToken;
      if (token) headers["Authorization"] = `Bearer ${token}`;
      return headers;
    },

    /** Map API row to UI shape (status from is_active / is_sold). */
    normalizeListingRow(item) {
      const isSold = item.is_sold === true;
      const isActive = item.is_active !== false;
      let status = "active";
      if (isSold) status = "sold";
      else if (!isActive) status = "inactive";
      const created = item.listing_created_at || item.created_at || null;
      const seller = item.seller || {
        name: "—",
        picture: null,
        verified: false,
        rating: 0,
        reviews: 0,
      };
      return {
        ...item,
        status,
        created_at: created,
        coverIndex: item.coverIndex ?? item.cover_index ?? 0,
        views: typeof item.views === "number" ? item.views : 0,
        save_count: typeof item.save_count === "number" ? item.save_count : 0,
        seller,
      };
    },

    /** Views / saves — matches backend integer counts (abbreviates large numbers). */
    formatCount(n) {
      const v = Number(n) || 0;
      if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
      if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
      return String(v);
    },

    formatListingDate(iso) {
      if (!iso) return "—";
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "—";
        return d.toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return "—";
      }
    },

    async fetchListings() {
      if (!this.hasSupportAccess) {
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const response = await fetch(buildApiUrl("/seller/admin/listings"), {
          headers: this.getAdminHeaders(false),
        });

        if (!response.ok) {
          const errText = await response.text().catch(() => "");
          throw new Error(errText || `HTTP ${response.status}`);
        }

        const data = await response.json();
        this.listings = Array.isArray(data) ? data.map((row) => this.normalizeListingRow(row)) : [];
      } catch (error) {
        console.error("Error fetching listings:", error);
        this.showToast("error", "Error", "Failed to load listings. Check admin login and permissions.");
        this.listings = [];
      } finally {
        this.loading = false;
      }
    },
    
    sortListings(listings) {
      return [...listings].sort((a, b) => {
        let aValue, bValue;
        
        switch (this.sortBy) {
          case 'price-high':
          case 'price-low':
            aValue = a.price;
            bValue = b.price;
            return this.sortBy === 'price-high' ? bValue - aValue : aValue - bValue;
            
          case 'oldest':
            return new Date(a.created_at || 0) - new Date(b.created_at || 0);

          case 'recent':
          default:
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        }
      });
    },
    
    sortByField(field) {
      if (this.sortBy === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = field;
        this.sortDirection = 'desc';
      }
    },
    
    performSearch() {
      this.currentPage = 1;
    },
    
    applyFilters() {
      this.currentPage = 1;
    },
    
    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
    },
    
    clearAllFilters() {
      this.searchQuery = '';
      this.statusFilter = '';
      this.categoryFilter = '';
      this.sortBy = 'recent';
      this.currentPage = 1;
    },
    
    toggleRowExpansion(listingId) {
      this.expandedRow = this.expandedRow === listingId ? null : listingId;
    },
    
    getCoverImage(listing) {
      if (listing.images && listing.images.length > 0) {
        const index = listing.coverIndex || 0;
        return listing.images[index];
      }
      return 'https://via.placeholder.com/300x200?text=No+Image';
    },
    
    previewImages(listing) {
      if (!listing.images || listing.images.length === 0) return;
      
      this.imagePreview = {
        listing_id: listing.listing_id,
        title: listing.title,
        sellerName: listing.seller.name,
        images: listing.images,
        imageUrl: listing.images[0],
        index: 0,
        total: listing.images.length
      };
    },
    
    previewSpecificImage(listing, index) {
      this.imagePreview = {
        listing_id: listing.listing_id,
        title: listing.title,
        sellerName: listing.seller.name,
        images: listing.images,
        imageUrl: listing.images[index],
        index,
        total: listing.images.length
      };
    },
    
    closeImagePreview() {
      this.imagePreview = null;
    },
    
    navigateImage(direction) {
      if (!this.imagePreview) return;
      
      const newIndex = this.imagePreview.index + direction;
      if (newIndex >= 0 && newIndex < this.imagePreview.total) {
        this.imagePreview.index = newIndex;
        this.imagePreview.imageUrl = this.imagePreview.images[newIndex];
      }
    },
    
    exportData() {
      this.showToast('success', 'Export', 'Exporting to CSV...');
    },
    
    refreshData() {
      this.fetchListings();
    },
    
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    
    goToPage(page) {
      this.currentPage = page;
    },
    
    getConditionClass(condition) {
      const classes = {
        'New': 'condition-new',
        'Like New': 'condition-like-new',
        'Good': 'condition-good',
        'Fair': 'condition-fair',
        'Used': 'condition-used'
      };
      return classes[condition] || 'condition-used';
    },
    
    getStatusClass(status) {
      const classes = {
        active: "status-active",
        inactive: "status-pending",
        sold: "status-suspended",
      };
      return classes[status] || "status-pending";
    },

    getStatusIcon(status) {
      const icons = {
        active: "fa-solid fa-check-circle",
        inactive: "fa-solid fa-pause-circle",
        sold: "fa-solid fa-circle-check",
      };
      return icons[status] || "fa-solid fa-clock";
    },

    getStatusText(status) {
      const texts = {
        active: "Active",
        inactive: "Inactive",
        sold: "Sold",
      };
      return texts[status] || status;
    },
    
    formatPrice(price) {
      return new Intl.NumberFormat().format(price);
    },
    
    getDefaultAvatar() {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSI1MCIgY3k9IjUwIiByPSI1MCIgZmlsbD0iI2RkZCIvPjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjIwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UzwvdGV4dD48L3N2Zz4=';
    },
    
    handleImageError(event) {
      event.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
    },
    
    handleSellerImageError(event) {
      event.target.src = this.getDefaultAvatar();
    },
    
  }
};
</script>

<style scoped>
.access-denied-card {
  max-width: 420px;
  margin: 60px auto;
  padding: 32px;
  background: var(--background-color2);
  border-radius: 12px;
  border: 1px solid var(--border-color2);
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.access-denied-icon {
  font-size: 48px;
  color: #f59e0b;
  margin-bottom: 16px;
}
.access-denied-card h3 {
  font-size: 1.25rem;
  color: var(--text-color1);
  margin: 0 0 12px 0;
}
.access-denied-card p {
  font-size: 0.9375rem;
  color: var(--text-color2);
  margin: 0 0 8px 0;
  line-height: 1.5;
}
.access-denied-hint {
  font-size: 0.8125rem;
  opacity: 0.85;
}

.admin-products-view {
  min-height: 100vh;
  background: var(--background-color1);
  color: var(--text-color1);
  padding: 15px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* HEADER STYLES - COMPACT */
.admin-header {
  margin-bottom: 20px;
}

.header-content {
  margin-bottom: 20px;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-color1);
}

.page-title i {
  color: var(--border-color-hover);
  font-size: 24px;
}

.page-subtitle {
  color: var(--text-color2);
  font-size: 14px;
  margin: 0;
}

.header-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.stat-card {
  background: var(--background-color2);
  border-radius: 10px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--border-color-hover1);
  transition: all 0.3s ease;
  box-shadow: 0 3px 10px var(--box-shadow);
}

.stat-card:hover {
  transform: translateY(-3px);
  border-color: var(--border-color-hover);
}

.stat-card i {
  font-size: 24px;
  color: var(--border-color-hover);
  background: var(--background-color1);
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-card div {
  flex: 1;
  min-width: 0;
}

.stat-number {
  display: block;
  font-size: 22px;
  font-weight: 800;
  color: var(--text-color1);
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 13px;
  color: var(--text-color2);
  font-weight: 500;
}

/* CONTROLS SECTION - COMPACT */
.controls-section {
  background: var(--background-color2);
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 20px;
  border: 1px solid var(--border-color-hover1);
  box-shadow: 0 3px 12px var(--box-shadow);
}

.search-filter-group {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 280px;
  position: relative;
  display: flex;
  align-items: center;
}

.search-box i {
  position: absolute;
  left: 14px;
  color: var(--text-color2);
  font-size: 16px;
}

.search-box input {
  width: 100%;
  padding: 12px 42px 12px 40px;
  border-radius: 8px;
  border: 2px solid var(--border-color-hover1);
  background: var(--background-color1);
  color: var(--text-color1);
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-box input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 2px rgba(var(--border-color-hover-rgb), 0.1);
}

.clear-btn {
  position: absolute;
  right: 10px;
  background: var(--background-color1);
  border: none;
  color: var(--text-color2);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;
}

.clear-btn:hover {
  background: var(--border-color-hover1);
  color: var(--text-color1);
}

.filter-group {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-select {
  position: relative;
  display: flex;
  align-items: center;
}

.filter-select i {
  position: absolute;
  left: 12px;
  color: var(--text-color2);
  font-size: 12px;
  z-index: 1;
}

.filter-select select {
  padding: 10px 16px 10px 34px;
  border-radius: 8px;
  border: 2px solid var(--border-color-hover1);
  background: var(--background-color1);
  color: var(--text-color1);
  font-size: 13px;
  cursor: pointer;
  min-width: 140px;
  appearance: none;
  transition: all 0.3s ease;
}

.filter-select select:focus {
  outline: none;
  border-color: var(--border-color-hover);
}

.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.btn-export, .btn-refresh {
  padding: 10px 18px;
  border-radius: 8px;
  border: 2px solid var(--border-color-hover);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn-export {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.btn-export:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

.btn-refresh {
  background: transparent;
  color: var(--border-color-hover);
}

.btn-refresh:hover {
  background: rgba(var(--border-color-hover-rgb), 0.1);
  transform: translateY(-2px);
}

/* CONTENT WRAPPER */
.content-wrapper {
  background: var(--background-color2);
  border-radius: 12px;
  padding: 18px;
  border: 1px solid var(--border-color-hover1);
  box-shadow: 0 3px 12px var(--box-shadow);
  min-height: 400px;
}

/* TABLE SCROLL WRAPPER */
.table-scroll-wrapper {
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid var(--border-color-hover1);
  margin: -1px;
}

.table-scroll-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-scroll-wrapper::-webkit-scrollbar-track {
  background: var(--background-color1);
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb {
  background: var(--border-color-hover1);
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: var(--border-color-hover);
}

/* TABLE VIEW WITH FIXED WIDTH COLUMNS */
.table-view {
  min-width: 1200px; /* Ensures table can be scrolled horizontally */
}

.table-header {
  background: var(--background-color1);
  border-bottom: 1px solid var(--border-color-hover1);
  position: sticky;
  top: 0;
  z-index: 10;
}

.header-row {
  display: grid;
  grid-template-columns: 250px 180px 120px 120px 120px 120px 120px 140px;
  padding: 12px 16px;
  font-weight: 600;
  font-size: 13px;
  color: var(--text-color1);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.table-cell {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.header-row .table-cell {
  cursor: pointer;
  user-select: none;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.header-row .table-cell:hover {
  color: var(--border-color-hover);
}

.header-row .table-cell i {
  margin-left: 6px;
  font-size: 11px;
  opacity: 0.7;
}

.table-body {
  background: var(--background-color2);
}

.listing-row {
  display: grid;
  grid-template-columns: 250px 180px 120px 120px 120px 120px 120px 140px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color-hover1);
  transition: all 0.3s ease;
  position: relative;
}

.listing-row:hover {
  background: var(--background-color1);
}

.listing-row.row-expanded {
  background: var(--background-color1);
  border-bottom: none;
}

/* COMPACT COMPONENT STYLES */

/* Product Column */
.product-col {
  align-items: flex-start;
}

.product-info-compact {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  min-width: 0;
}

.product-image-compact {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid var(--border-color-hover1);
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.product-image-compact:hover {
  border-color: var(--border-color-hover);
  transform: scale(1.05);
}

.product-image-compact img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-count-compact {
  position: absolute;
  bottom: 3px;
  right: 3px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 10px;
  display: flex;
  align-items: center;
  gap: 1px;
}

.product-details-compact {
  flex: 1;
  min-width: 0;
}

.product-title-compact {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
  color: var(--text-color1);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.product-brand-compact {
  font-size: 12px;
  color: var(--text-color2);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-meta-compact {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meta-item-compact {
  font-size: 11px;
  color: var(--text-color2);
  display: flex;
  align-items: center;
  gap: 3px;
}

/* Seller Column */
.seller-info-compact {
  display: flex;
  gap: 10px;
  align-items: center;
}

.seller-avatar-compact {
  position: relative;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.seller-avatar-compact img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border-color-hover1);
}

.verified-badge-compact {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  background: var(--border-color-hover3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--background-color2);
}

.verified-badge-compact i {
  color: var(--text-color3);
  font-size: 8px;
}

.seller-details-compact {
  flex: 1;
  min-width: 0;
}

.seller-name-compact {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 3px;
  color: var(--text-color1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.seller-rating-compact {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--text-color2);
}

.seller-rating-compact i {
  color: gold;
  font-size: 10px;
}

/* Category Column */
.category-badge-compact {
  padding: 5px 10px;
  background: var(--background-color1);
  border: 1px solid var(--border-color-hover1);
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-color1);
  display: inline-block;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Price Column */
.price-col {
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.price-amount-compact {
  font-size: 16px;
  font-weight: 700;
  color: var(--border-color-hover);
  line-height: 1;
}

.negotiable-badge-compact {
  padding: 3px 8px;
  background: rgba(var(--border-color-hover3-rgb), 0.1);
  border: 1px solid var(--border-color-hover3);
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  color: var(--border-color-hover3);
  display: inline-flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
}

/* Condition Column */
.condition-badge-compact {
  padding: 5px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  display: inline-block;
  border: 1px solid;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.condition-new {
  background: rgba(0, 151, 0, 0.1);
  border-color: rgb(0, 151, 0);
  color: rgb(0, 151, 0);
}

.condition-like-new {
  background: rgba(0, 255, 255, 0.1);
  border-color: aqua;
  color: aqua;
}

.condition-good {
  background: rgba(255, 215, 0, 0.1);
  border-color: gold;
  color: gold;
}

.condition-fair {
  background: rgba(245, 158, 11, 0.1);
  border-color: #f59e0b;
  color: #f59e0b;
}

.condition-used {
  background: rgba(148, 163, 184, 0.1);
  border-color: #94a3b8;
  color: #94a3b8;
}

/* Status Column */
.status-badge-compact {
  padding: 5px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid;
  white-space: nowrap;
}

.status-active {
  background: rgba(0, 151, 0, 0.1);
  border-color: rgb(0, 151, 0);
  color: rgb(0, 151, 0);
}

.status-pending {
  background: rgba(245, 158, 11, 0.1);
  border-color: #f59e0b;
  color: #f59e0b;
}

.status-suspended {
  background: rgba(220, 38, 38, 0.1);
  border-color: #dc2626;
  color: #dc2626;
}

/* Analytics Column */
.analytics-compact {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.analytic-item-compact {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-color2);
}

.analytic-item-compact i {
  color: var(--border-color-hover);
  font-size: 12px;
  width: 16px;
}

.analytic-item-compact span {
  font-weight: 500;
  color: var(--text-color1);
}

/* Actions Column */
.action-buttons-compact {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
}

.btn-action-compact {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color1);
  color: var(--text-color2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.btn-action-compact:hover {
  transform: translateY(-2px);
}

.view-btn:hover {
  background: var(--border-color-hover1);
  color: var(--border-color-hover);
  border-color: var(--border-color-hover);
}

.edit-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-color: #3b82f6;
}

.delete-btn:hover {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
  border-color: #dc2626;
}

.more-btn:hover {
  background: var(--border-color-hover1);
  color: var(--text-color1);
}

.dropdown-compact {
  position: relative;
}

.dropdown-menu-compact {
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover1);
  border-radius: 6px;
  padding: 6px;
  min-width: 160px;
  z-index: 100;
  box-shadow: 0 6px 24px var(--box-shadow);
  display: none;
}

.dropdown-compact:hover .dropdown-menu-compact {
  display: block;
}

.dropdown-item-compact {
  width: 100%;
  padding: 8px 12px;
  text-align: left;
  background: none;
  border: none;
  color: var(--text-color1);
  font-size: 12px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.dropdown-item-compact:hover {
  background: var(--background-color1);
  color: var(--border-color-hover);
}

/* EXPANDED DETAILS - COMPACT */
.expanded-details-compact {
  grid-column: 1 / -1;
  padding: 18px;
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover);
  border-radius: 10px;
  margin-top: 12px;
  animation: slideDown 0.3s ease;
}

.details-grid-compact {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.detail-section-compact {
  background: var(--background-color1);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid var(--border-color-hover1);
}

.detail-section-compact h6 {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 6px;
}

.description-content-compact p {
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-color2);
}

.image-gallery-compact {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.gallery-thumbnail-compact {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.gallery-thumbnail-compact:hover {
  transform: scale(1.05);
  border-color: var(--border-color-hover1);
}

.gallery-thumbnail-compact.active {
  border-color: var(--border-color-hover);
  box-shadow: 0 3px 10px rgba(var(--border-color-hover-rgb), 0.2);
}

.gallery-thumbnail-compact img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-number-compact {
  position: absolute;
  top: 3px;
  right: 3px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}

.analytics-grid-compact {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.analytic-item-expanded {
  background: var(--background-color2);
  padding: 12px;
  border-radius: 6px;
  border: 1px solid var(--border-color-hover1);
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
}

.analytic-item-expanded:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-2px);
}

.analytic-item-expanded i {
  font-size: 18px;
  color: var(--border-color-hover);
  width: 36px;
  height: 36px;
  background: var(--background-color1);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.analytic-value-expanded {
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-color1);
  line-height: 1;
  margin-bottom: 2px;
}

.analytic-label-expanded {
  font-size: 11px;
  color: var(--text-color2);
}

.info-grid-compact {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item-compact {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  background: var(--background-color2);
  border-radius: 6px;
  border: 1px solid var(--border-color-hover1);
}

.info-item-compact i {
  color: var(--border-color-hover);
  font-size: 14px;
  width: 24px;
  flex-shrink: 0;
}

.info-item-compact span:first-of-type {
  font-size: 12px;
  color: var(--text-color2);
  min-width: 50px;
}

.info-item-compact span:last-of-type {
  font-size: 13px;
  color: var(--text-color1);
  font-weight: 500;
}

.detail-actions-compact {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 15px;
  border-top: 1px solid var(--border-color-hover1);
}

.btn-primary-compact, .btn-secondary-compact {
  padding: 10px 18px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn-primary-compact {
  background: var(--border-color-hover);
  border: 2px solid var(--border-color-hover);
  color: var(--text-color3);
}

.btn-primary-compact:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

.btn-secondary-compact {
  background: transparent;
  border: 2px solid var(--border-color-hover1);
  color: var(--text-color1);
}

.btn-secondary-compact:hover {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  transform: translateY(-2px);
}

/* PAGINATION - COMPACT */
.pagination-compact {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid var(--border-color-hover1);
}

.pagination-info {
  font-size: 13px;
  color: var(--text-color2);
  text-align: center;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.pagination-btn-compact {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color1);
  color: var(--text-color1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;
}

.pagination-btn-compact:hover:not(:disabled) {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  transform: translateY(-2px);
}

.pagination-btn-compact:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers-compact {
  display: flex;
  gap: 6px;
  align-items: center;
}

.page-number-compact {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color1);
  color: var(--text-color1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.page-number-compact:hover {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

.page-number-compact.active {
  background: var(--border-color-hover);
  border-color: var(--border-color-hover);
  color: var(--text-color3);
}

/* MODALS (same as before, but kept for reference) */
.image-preview-modal,
.confirmation-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 15px;
}

.modal-content {
  background: var(--background-color2);
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  border: 1px solid var(--border-color-hover1);
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
  animation: modalIn 0.3s ease;
}

.modal-header {
  padding: 18px;
  border-bottom: 1px solid var(--border-color-hover1);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-color1);
  margin: 0;
}

.modal-header.warning {
  background: rgba(245, 158, 11, 0.1);
  border-color: #f59e0b;
}

.modal-header.warning i {
  color: #f59e0b;
  font-size: 20px;
  margin-right: 10px;
}

.modal-close {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color1);
  color: var(--text-color1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.modal-close:hover {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  transform: rotate(90deg);
}

.modal-body {
  padding: 18px;
  max-height: 60vh;
  overflow-y: auto;
}

.preview-image-container {
  position: relative;
  margin-bottom: 18px;
}

.preview-image-container img {
  width: 100%;
  height: 300px;
  object-fit: contain;
  background: var(--background-color1);
  border-radius: 10px;
  border: 1px solid var(--border-color-hover1);
}

.image-nav {
  position: absolute;
  bottom: 15px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.nav-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid var(--border-color-hover1);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  font-size: 14px;
}

.nav-btn:hover:not(:disabled) {
  border-color: var(--border-color-hover);
  background: rgba(0, 0, 0, 0.9);
  transform: scale(1.1);
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.image-counter {
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 600;
  backdrop-filter: blur(10px);
}

.warning-text {
  color: #f59e0b !important;
  font-size: 13px !important;
  margin-top: 10px !important;
  padding: 10px;
  background: rgba(245, 158, 11, 0.1);
  border-radius: 6px;
  border-left: 3px solid #f59e0b;
}

.modal-footer {
  padding: 18px;
  border-top: 1px solid var(--border-color-hover1);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-cancel, .btn-confirm {
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel {
  background: transparent;
  border: 2px solid var(--border-color-hover1);
  color: var(--text-color1);
}

.btn-cancel:hover {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

.btn-confirm {
  background: #dc2626;
  border: 2px solid #dc2626;
  color: white;
}

.btn-confirm:hover {
  background: #b91c1c;
  border-color: #b91c1c;
  transform: translateY(-2px);
}

/* TOAST NOTIFICATION */
.toast-notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: var(--background-color2);
  border-radius: 10px;
  padding: 15px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 350px;
  border: 1px solid;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  z-index: 1001;
  animation: toastIn 0.3s ease;
}

.toast-notification.success {
  border-color: rgb(0, 151, 0);
  border-left: 4px solid rgb(0, 151, 0);
}

.toast-notification.error {
  border-color: #dc2626;
  border-left: 4px solid #dc2626;
}

.toast-notification.warning {
  border-color: #f59e0b;
  border-left: 4px solid #f59e0b;
}

.toast-notification.info {
  border-color: var(--border-color-hover);
  border-left: 4px solid var(--border-color-hover);
}

.toast-notification i {
  font-size: 20px;
  margin-top: 2px;
  flex-shrink: 0;
}

.toast-notification.success i {
  color: rgb(0, 151, 0);
}

.toast-notification.error i {
  color: #dc2626;
}

.toast-notification.warning i {
  color: #f59e0b;
}

.toast-notification.info i {
  color: var(--border-color-hover);
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--text-color1);
}

.toast-message {
  font-size: 13px;
  color: var(--text-color2);
  margin: 0;
  line-height: 1.4;
}

.toast-close {
  background: none;
  border: none;
  color: var(--text-color2);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.3s ease;
  font-size: 12px;
  flex-shrink: 0;
}

.toast-close:hover {
  color: var(--text-color1);
  background: var(--background-color1);
}

/* LOADING STATE */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 15px;
  text-align: center;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid var(--border-color-hover1);
  border-top-color: var(--border-color-hover);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner i {
  color: var(--border-color-hover);
  font-size: 20px;
}

.loading-state p {
  color: var(--text-color1);
  font-size: 14px;
  margin-top: 12px;
}

/* EMPTY STATE */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 15px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: var(--background-color1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  border: 2px solid var(--border-color-hover1);
}

.empty-icon i {
  font-size: 36px;
  color: var(--text-color2);
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 10px;
  color: var(--text-color1);
}

.empty-state p {
  color: var(--text-color2);
  margin-bottom: 20px;
  font-size: 14px;
}

.btn-clear-filters {
  padding: 10px 20px;
  background: var(--background-color1);
  border: 2px solid var(--border-color-hover1);
  border-radius: 6px;
  color: var(--text-color1);
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  font-size: 13px;
}

.btn-clear-filters:hover {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  transform: translateY(-2px);
}

/* ANIMATIONS */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes modalIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(15px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes toastIn {
  from {
    opacity: 0;
    transform: translateX(80px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* TRANSITIONS */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.toast-enter-active, .toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from, .toast-leave-to {
  opacity: 0;
  transform: translateX(80px);
}

.slide-down-enter-active {
  animation: slideDown 0.3s ease;
}

.slide-down-leave-active {
  animation: slideDown 0.3s ease reverse;
}

/* RESPONSIVE DESIGN */
@media (max-width: 768px) {
  .admin-products-view {
    padding: 12px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .header-stats {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  }
  
  .stat-card {
    padding: 12px;
  }
  
  .stat-card i {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
  
  .controls-section {
    padding: 15px;
  }
  
  .search-box {
    min-width: 100%;
  }
  
  .filter-select select {
    min-width: 120px;
    padding: 9px 14px 9px 30px;
  }
  
  .content-wrapper {
    padding: 15px;
  }
  
  .table-scroll-wrapper {
    margin: -2px;
  }
  
  .table-view {
    min-width: 1100px;
  }
  
  .header-row,
  .listing-row {
    grid-template-columns: 230px 170px 110px 110px 110px 110px 110px 130px;
    padding: 10px 14px;
  }
  
  .toast-notification {
    left: 12px;
    right: 12px;
    max-width: none;
    bottom: 15px;
  }
}

@media (max-width: 480px) {
  .header-stats {
    grid-template-columns: 1fr;
  }
  
  .filter-group {
    justify-content: center;
  }
  
  .action-buttons {
    justify-content: center;
  }
  
  .btn-export, .btn-refresh {
    padding: 9px 15px;
    font-size: 12px;
  }
  
  .table-view {
    min-width: 1000px;
  }
  
  .header-row,
  .listing-row {
    grid-template-columns: 200px 150px 100px 100px 100px 100px 100px 120px;
    font-size: 12px;
  }
  
  .product-image-compact {
    width: 50px;
    height: 50px;
  }
  
  .product-title-compact {
    font-size: 13px;
  }
  
  .seller-avatar-compact {
    width: 36px;
    height: 36px;
  }
  
  .price-amount-compact {
    font-size: 15px;
  }
  
  .btn-action-compact {
    width: 30px;
    height: 30px;
  }
}
</style>